import React, { useState } from "react";
import { Navigate } from "react-router-dom";
import {
  Alert,
  Box,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { Controller, useForm } from "react-hook-form";
import firebase from "firebase/compat/app";
import "firebase/compat/functions";
import { format } from "date-fns";
import DashboardPageHeader from "components/common/DashboardPageHeader";
import { useAuth } from "hooks/useAuth";

const SupportAccessSettingsPage = () => {
  const { userData, organization, organizationId } = useAuth();
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const { control, handleSubmit, reset } = useForm({
    defaultValues: {
      ticketId: "",
      accessedBy: "",
      reason: "",
    },
  });

  if (userData?.role !== "admin") {
    return <Navigate to="/dashboard/settings" replace />;
  }

  const log = Array.isArray(organization?.supportAccessLog)
    ? organization.supportAccessLog
    : [];

  const onApprove = async (values) => {
    setSubmitting(true);
    setErrorMessage("");
    setSuccessMessage("");

    try {
      const callable = firebase
        .app()
        .functions("us-central1")
        .httpsCallable("approveSupportAccess");

      await callable({
        organizationId,
        ticketId: values.ticketId.trim(),
        accessedBy: values.accessedBy.trim(),
        reason: values.reason.trim(),
      });

      setSuccessMessage("Support access approved for this incident.");
      reset();
    } catch (err) {
      setErrorMessage(err.message || "Unable to approve support access.");
    } finally {
      setSubmitting(false);
    }
  };

  const onRevoke = async (entry) => {
    setSubmitting(true);
    setErrorMessage("");
    setSuccessMessage("");

    try {
      const callable = firebase
        .app()
        .functions("us-central1")
        .httpsCallable("revokeSupportAccess");

      await callable({
        organizationId,
        ticketId: entry.ticketId,
        accessedBy: entry.accessedBy,
      });

      setSuccessMessage("Support access revoked.");
    } catch (err) {
      setErrorMessage(err.message || "Unable to revoke support access.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box>
      <DashboardPageHeader
        title="Support access"
        subtitle="Grant time-limited ASI support staff access to diagnose a specific reported problem. Each grant is logged permanently."
      />

      {successMessage && (
        <Alert severity="success" onClose={() => setSuccessMessage("")} sx={{ mb: 2 }}>
          {successMessage}
        </Alert>
      )}
      {errorMessage && (
        <Alert severity="error" onClose={() => setErrorMessage("")} sx={{ mb: 2 }}>
          {errorMessage}
        </Alert>
      )}

      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Approve support access
        </Typography>
        <Stack
          component="form"
          spacing={2}
          onSubmit={handleSubmit(onApprove)}
          sx={{ maxWidth: 560 }}
        >
          <Controller
            name="ticketId"
            control={control}
            rules={{ required: "Ticket ID is required." }}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                label="Support ticket ID"
                error={Boolean(fieldState.error)}
                helperText={fieldState.error?.message}
              />
            )}
          />
          <Controller
            name="accessedBy"
            control={control}
            rules={{ required: "Staff user ID is required." }}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                label="ASI staff user ID"
                error={Boolean(fieldState.error)}
                helperText={fieldState.error?.message}
              />
            )}
          />
          <Controller
            name="reason"
            control={control}
            rules={{ required: "Reason is required." }}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                label="Reason"
                multiline
                minRows={3}
                error={Boolean(fieldState.error)}
                helperText={fieldState.error?.message}
              />
            )}
          />
          <LoadingButton type="submit" variant="contained" loading={submitting}>
            Approve access (72 hours)
          </LoadingButton>
        </Stack>
      </Paper>

      <Paper sx={{ p: 2 }}>
        <Typography variant="h6" gutterBottom>
          Access log
        </Typography>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Ticket</TableCell>
              <TableCell>Staff</TableCell>
              <TableCell>Reason</TableCell>
              <TableCell>Expires</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {log.length === 0 && (
              <TableRow>
                <TableCell colSpan={6}>
                  <Typography variant="body2" color="text.secondary">
                    No support access entries yet.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {log.map((entry, index) => {
              const expiresAt = entry.expiresAt?.toDate?.() || null;
              const revoked = Boolean(entry.revokedAt);
              return (
                <TableRow key={`${entry.ticketId}-${index}`}>
                  <TableCell>{entry.ticketId}</TableCell>
                  <TableCell>{entry.accessedBy}</TableCell>
                  <TableCell>{entry.reason}</TableCell>
                  <TableCell>
                    {expiresAt ? format(expiresAt, "MMM d, yyyy HH:mm") : "—"}
                  </TableCell>
                  <TableCell>{revoked ? "Revoked" : "Active"}</TableCell>
                  <TableCell align="right">
                    {!revoked && (
                      <LoadingButton
                        size="small"
                        color="error"
                        loading={submitting}
                        onClick={() => onRevoke(entry)}
                      >
                        Revoke
                      </LoadingButton>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Paper>
    </Box>
  );
};

export default SupportAccessSettingsPage;
